import { transparentize } from 'polished'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import styled, { DefaultTheme } from 'styled-components'

import Popover, { PopoverProps } from '../Popover'

export const TooltipContainer = styled.div<{ theme: DefaultTheme }>`
  max-width: 256px;
  padding: 0.6rem 1rem;
  font-weight: 400;
  word-break: break-word;
  font-size: 14px;

  background: ${({ theme }) => theme.cardSecondary};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  box-shadow: 0 4px 8px 0 ${({ theme }) => transparentize(0.9, theme.shadow1)};
`

interface TooltipProps extends Omit<PopoverProps, 'content'> {
  text: ReactNode
  disableHover?: boolean // disable the hover and content display
  onTooltipMouseEnter?: () => void
  onTooltipMouseLeave?: () => void
}

interface TooltipContentProps extends Omit<PopoverProps, 'content'> {
  content: ReactNode
  onOpen?: () => void
  // whether to wrap the content in a `TooltipContainer`
  wrap?: boolean
  disableHover?: boolean // disable the hover and content display
}

export default function Tooltip({ text, onTooltipMouseEnter, onTooltipMouseLeave, ...rest }: TooltipProps) {
  return (
    <Popover
      content={
        text && (
          <TooltipContainer onMouseEnter={onTooltipMouseEnter} onMouseLeave={onTooltipMouseLeave}>
            {text}
          </TooltipContainer>
        )
      }
      {...rest}
    />
  )
}

function TooltipContent({ content, wrap = false, ...rest }: TooltipContentProps) {
  return <Popover content={wrap ? <TooltipContainer>{content}</TooltipContainer> : content} {...rest} />
}

/** Standard text tooltip. */
export function MouseoverTooltip({
  text,
  disableHover,
  children,
  delay = 0,
  ...rest
}: Omit<TooltipProps, 'show'> & { delay?: number }) {
  const [show, setShow] = useState(false)
  const timeoutRef = useRef<any>()

  const open = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    if (delay > 0) {
      timeoutRef.current = setTimeout(() => setShow(true), delay)
    } else {
      setShow(true)
    }
  }, [delay])

  const close = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setShow(false)
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <Tooltip {...rest} show={show} text={disableHover ? null : text}>
      <div onMouseEnter={disableHover ? undefined : open} onMouseLeave={disableHover ? undefined : close}>
        {children}
      </div>
    </Tooltip>
  )
}

/** Tooltip that displays custom content. */
export function MouseoverTooltipContent({
  content,
  children,
  onOpen: openCallback = undefined,
  disableHover,
  ...rest
}: Omit<TooltipContentProps, 'show'>) {
  const [show, setShow] = useState(false)
  const open = useCallback(() => {
    setShow(true)
    openCallback?.()
  }, [openCallback])
  const close = useCallback(() => setShow(false), [setShow])
  const shouldShow = !disableHover && !!content && show

  return (
    <TooltipContent {...rest} show={shouldShow} content={disableHover ? null : content} showArrow={!!content}>
      <div
        style={{ display: 'inline-block', lineHeight: 0, padding: '0.25rem', width: '100%' }}
        onMouseEnter={open}
        onMouseLeave={close}
      >
        {children}
      </div>
    </TooltipContent>
  )
}
